import React from "react";
import { CheckCircle } from "lucide-react";
import { PINNED_SIDEBAR_STICKY } from "./PinnedNavigation";

// Section stepper shared by the assessment questionnaires: one entry per question
// category, showing answered/visible counts and allowing jumps into a section — landing
// on the last question the user actually answered there (see buildAssessmentSections'
// `jumpQuestion`), so returning to a partially-filled category resumes near where the
// user left off instead of always restarting at question 1. The active section also gets
// a "jump to question N" dropdown (categories can run 20-30+ questions, too many to reach
// one Next click at a time). `getQuestionId` lets each flow supply its own question-id
// field name. `stickyClassName` sets where the list pins and how tall it may grow; the default
// clears the pinned top menu.
const AssessmentSectionSidebar = ({
    sections,
    currentQuestionId,
    onJumpToQuestion,
    getQuestionId = (q) => q.id,
    stickyClassName = PINNED_SIDEBAR_STICKY,
}) => {
    if (!sections || sections.length === 0) return null;

    return (
        <nav className={`bg-white rounded-lg border border-gray-200 p-4 sticky ${stickyClassName} overflow-y-auto`}>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Sections</h4>
            <ul className="space-y-1">
                {sections.map(section => {
                    const isCurrent = section.questions.some(q => getQuestionId(q) === currentQuestionId);
                    const isComplete =
                        section.questions.length > 0 && section.answeredCount === section.questions.length;
                    return (
                        <li key={section.category}>
                            <button
                                type="button"
                                onClick={() => onJumpToQuestion(getQuestionId(section.jumpQuestion ?? section.questions[0]))}
                                title={section.category}
                                className={`w-full flex items-start justify-between text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isCurrent
                                        ? "bg-blue-50 text-blue-800 font-medium"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                <span className="mr-2">{section.category}</span>
                                {isComplete ? (
                                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                    <span className="text-xs text-gray-500 flex-shrink-0 mt-0.5">
                                        {section.answeredCount}/{section.questions.length}
                                    </span>
                                )}
                            </button>
                            {/* Only for the active section, and only when it's worth it (Previous/
                                Next alone means clicking through every question one at a time to
                                reach, say, question 20 of 23) — a direct jump-to-question picker. */}
                            {isCurrent && section.questionOptions?.length >= 2 && (
                                <select
                                    aria-label={`Jump to a specific question in ${section.category}`}
                                    value={String(currentQuestionId ?? "")}
                                    onChange={(e) => {
                                        const target = section.questionOptions.find(
                                            (opt) => String(opt.id) === e.target.value
                                        );
                                        if (target) onJumpToQuestion(target.id);
                                    }}
                                    className="w-full mt-1 mb-1.5 text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 bg-gray-50 cursor-pointer"
                                >
                                    {section.questionOptions.map((opt) => (
                                        <option key={opt.id} value={opt.id} title={opt.text}>
                                            {opt.answered ? "✓ " : ""}Question {opt.position}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default AssessmentSectionSidebar;
